import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Trophy, XCircle, BarChart3, ArrowDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DAY_OPTIONS = [
  { value: 7, label: '7 Tage' },
  { value: 30, label: '30 Tage' },
  { value: 90, label: '90 Tage' },
  { value: 365, label: '1 Jahr' },
  { value: 0, label: 'Gesamt' },
];

export default function AnalyticsDashboard({ inline }) {
  const [days, setDays] = useState(30);
  const [funnel, setFunnel] = useState(null);
  const [sources, setSources] = useState(null);
  const [winLoss, setWinLoss] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [f, s, wl] = await Promise.all([
        axios.get(`${API}/analytics/funnel`, { headers: authHeaders(), params: { days } }),
        axios.get(`${API}/analytics/source-breakdown`, { headers: authHeaders(), params: { days } }),
        axios.get(`${API}/analytics/win-loss`, { headers: authHeaders(), params: { days: days || 365 } }),
      ]);
      setFunnel(f.data);
      setSources(s.data);
      setWinLoss(wl.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Laden');
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const maxStageCount = funnel ? Math.max(1, ...funnel.stages.map(s => s.count)) : 1;
  const maxSourceTotal = sources?.sources?.length ? Math.max(1, ...sources.sources.map(s => s.total)) : 1;

  return (
    <div className={inline ? "p-3 md:p-6 max-w-5xl mx-auto w-full" : "min-h-screen bg-[#f5f5f5] p-3 md:p-6"}>
      {/* Header */}
      <div className="mb-4 md:mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Analytics
          </p>
          <h1 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Pipeline · Conversion · Quellen
          </h1>
        </div>
        {/* Zeit-Toggle */}
        <div className="flex gap-1 flex-wrap">
          {DAY_OPTIONS.map(o => (
            <button key={o.value} onClick={() => setDays(o.value)}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                days === o.value ? 'bg-[#0a0a0a] text-[#befa0a]' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-gray-500 text-sm p-6">Lädt…</p>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4">{error}</div>}

      {!loading && !error && funnel && (
        <>
          {/* KPI-Karten */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-6">
            <div className="bg-white border border-gray-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Leads gesamt</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{funnel.total}</p>
            </div>
            <div className="bg-white border border-gray-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Kunden</p>
              <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "'Oswald', sans-serif" }}>{funnel.customers}</p>
            </div>
            <div className="bg-white border border-gray-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Conversion</p>
              <p className="text-2xl font-bold text-[#658a07] flex items-center gap-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                <TrendingUp size={18} /> {funnel.conversion_rate}%
              </p>
            </div>
            <div className="bg-white border border-gray-200 p-3">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Verloren</p>
              <p className="text-2xl font-bold text-red-500 flex items-center gap-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                <TrendingDown size={18} /> {funnel.loss_rate}%
              </p>
            </div>
          </div>

          {/* Funnel */}
          <div className="bg-white border border-gray-200 p-4 md:p-6 mb-4 md:mb-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Akquise-Funnel
              </h2>
            </div>
            <div className="space-y-2">
              {funnel.stages.map((stage, i) => {
                const widthPct = Math.max(6, (stage.count / maxStageCount) * 100);
                const prevCount = i > 0 ? funnel.stages[i - 1].count : null;
                const dropPct = (prevCount && prevCount > 0)
                  ? Math.round((stage.count / prevCount) * 100)
                  : null;
                return (
                  <div key={stage.key}>
                    {i > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-gray-400 pl-1 py-0.5">
                        <ArrowDown size={10} />
                        {dropPct !== null ? `${dropPct}% weiter` : ''}
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <div className="w-24 md:w-28 flex-shrink-0 text-xs font-bold uppercase tracking-wider text-gray-600" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {stage.label}
                      </div>
                      <div className="flex-1 bg-gray-100 h-8 relative overflow-hidden">
                        <div
                          className={`h-full flex items-center px-2 transition-all ${stage.key === 'kunde' ? 'bg-emerald-500' : 'bg-[#0a0a0a]'}`}
                          style={{ width: `${widthPct}%` }}>
                          <span className={`text-xs font-bold ${stage.key === 'kunde' ? 'text-white' : 'text-[#befa0a]'}`} style={{ fontFamily: "'Oswald', sans-serif" }}>
                            {stage.count}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {/* Verloren separat */}
              <div className="flex items-center gap-3 pt-3 mt-3 border-t border-gray-100">
                <div className="w-24 md:w-28 flex-shrink-0 text-xs font-bold uppercase tracking-wider text-red-400" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Verloren
                </div>
                <div className="flex-1 bg-gray-100 h-8 relative overflow-hidden">
                  <div className="h-full bg-red-400 flex items-center px-2" style={{ width: `${Math.max(6, (funnel.lost / maxStageCount) * 100)}%` }}>
                    <span className="text-xs font-bold text-white" style={{ fontFamily: "'Oswald', sans-serif" }}>{funnel.lost}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Source-Breakdown */}
          <div className="bg-white border border-gray-200 p-4 md:p-6 mb-4 md:mb-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={18} className="text-[#658a07]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Quellen-Vergleich
              </h2>
            </div>
            {(!sources?.sources?.length) ? (
              <p className="text-gray-500 text-sm">Keine Daten im Zeitraum.</p>
            ) : (
              <div className="space-y-3">
                {sources.sources.map(s => (
                  <div key={s.source}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-gray-700" style={{ fontFamily: "'Oswald', sans-serif" }}>{s.source}</span>
                      <span className="text-xs text-gray-500">
                        {s.total} Leads · <span className="text-emerald-600 font-bold">{s.customers} Kunden</span> · {s.conversion_rate}%
                      </span>
                    </div>
                    <div className="bg-gray-100 h-5 relative overflow-hidden">
                      <div className="h-full bg-[#0a0a0a]" style={{ width: `${(s.total / maxSourceTotal) * 100}%` }} />
                      <div className="absolute top-0 left-0 h-full bg-emerald-500/70" style={{ width: `${(s.customers / maxSourceTotal) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Win/Loss */}
          {winLoss && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {/* Won */}
              <div className="bg-white border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy size={16} className="text-emerald-600" />
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Gewonnen ({winLoss.won_count})
                  </h2>
                </div>
                {winLoss.won.length === 0 ? (
                  <p className="text-gray-400 text-xs">Noch keine Kunden im Zeitraum.</p>
                ) : (
                  <div className="space-y-1.5">
                    {winLoss.won.map(w => (
                      <div key={w.id} className="flex items-center justify-between text-xs border-b border-gray-50 pb-1.5">
                        <span className="font-bold text-[#1f1f1f]">{w.company_name}</span>
                        <span className="text-gray-400">{w.source}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Lost */}
              <div className="bg-white border border-gray-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle size={16} className="text-red-500" />
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                    Verloren ({winLoss.lost_count})
                  </h2>
                </div>
                {winLoss.lost.length === 0 ? (
                  <p className="text-gray-400 text-xs">Keine verlorenen Leads im Zeitraum.</p>
                ) : (
                  <div className="space-y-2">
                    {winLoss.lost.map(l => (
                      <div key={l.id} className="text-xs border-b border-gray-50 pb-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-[#1f1f1f]">{l.company_name}</span>
                          <span className="text-gray-400">{l.source}</span>
                        </div>
                        {l.notes && <p className="text-gray-500 mt-0.5 line-clamp-2">{l.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
